import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Mail } from 'lucide-react';
import { subscribeToNewsletter } from '../services/beehiivService';

interface LeadCaptureFormProps {
  onSubmit: (data: { name: string; email: string }) => void;
  onSkip?: () => void;
  showSkip?: boolean;
}

export function LeadCaptureForm({ onSubmit, onSkip, showSkip = true }: LeadCaptureFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaValue) {
      setError('Please complete the CAPTCHA');
      return;
    }
    if (!name || !email) {
      setError('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      // Subscribe to Beehiiv newsletter
      await subscribeToNewsletter({
        email,
        name,
        reactivate: true
      });
      
      onSubmit({ name, email });
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
      <div className="flex items-center justify-center mb-6">
        <Mail className="h-12 w-12 text-indigo-600" />
      </div>
      
      <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
        Get Personalized Grant Updates
      </h2>
      
      <p className="text-gray-600 text-center mb-6">
        Stay informed about new grants matching your criteria and receive expert funding tips.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Full Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="John Doe"
            required
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="john@example.com"
            required
          />
        </div>

        <div className="flex justify-center">
          <ReCAPTCHA
            sitekey="6LfddncqAAAAAIhyhscMUMizdzgVicIVkd2q0UIT"
            onChange={(value) => {
              setCaptchaValue(value);
              setError('');
            }}
          />
        </div>

        {error && (
          <p className="text-red-600 text-sm text-center">{error}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting...' : 'Get Grant Updates'}
        </button>

        {showSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="w-full text-sm text-gray-500 hover:text-gray-700 text-center mt-2"
          >
            Skip for now
          </button>
        )}
      </form>

      <p className="mt-4 text-xs text-gray-500 text-center">
        You can unsubscribe at any time.
      </p>
    </div>
  );
}